/* Chat session store.
 *
 * Holds the rolling list of messages plus connection state. Tokens stream
 * through `appendDelta`, which mutates the last assistant bubble in place
 * (Zustand's setter is fine here — we still trigger a new array reference).
 */

import { create } from 'zustand';
import type { ChatMessage, ChatStreamEvent } from '../../lib/types';

interface ChatStore {
  messages: ChatMessage[];
  /** True between user submit and the final message_end event. */
  streaming: boolean;
  /** Latest WS state for the indicator dot. */
  socketState: 'connecting' | 'open' | 'closing' | 'closed';
  /** Mic permission / capture state. */
  recording: boolean;

  reset: () => void;
  pushUser: (content: string) => string; // returns local id
  pushAssistantStub: () => string;
  applyEvent: (ev: ChatStreamEvent) => void;
  setSocketState: (s: ChatStore['socketState']) => void;
  setRecording: (r: boolean) => void;
}

let localCounter = 1;
const localId = () => `local-${localCounter++}`;

export const useChat = create<ChatStore>((set) => ({
  messages: [],
  streaming: false,
  socketState: 'closed',
  recording: false,

  reset: () => set({ messages: [], streaming: false }),

  pushUser: (content) => {
    const id = localId();
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id,
          session_id: 'local',
          role: 'user',
          content,
          created_at: new Date().toISOString(),
        },
      ],
      streaming: true,
    }));
    return id;
  },

  pushAssistantStub: () => {
    const id = localId();
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id,
          session_id: 'local',
          role: 'assistant',
          content: '',
          created_at: new Date().toISOString(),
          pending: true,
        },
      ],
    }));
    return id;
  },

  applyEvent: (ev) =>
    set((s) => {
      switch (ev.kind) {
        case 'message_start': {
          // Replace the last pending assistant stub with the server's row, or
          // append if none exists.
          const next = [...s.messages];
          const lastIdx = next.length - 1;
          if (
            lastIdx >= 0 &&
            next[lastIdx].role === 'assistant' &&
            next[lastIdx].pending
          ) {
            next[lastIdx] = { ...ev.message, pending: true };
          } else {
            next.push({ ...ev.message, pending: true });
          }
          return { messages: next };
        }
        case 'token': {
          const next = [...s.messages];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].role === 'assistant' && next[i].pending) {
              next[i] = { ...next[i], content: next[i].content + ev.delta };
              break;
            }
          }
          return { messages: next };
        }
        case 'message_end': {
          const next = s.messages.map((m) =>
            m.role === 'assistant' && m.pending ? { ...m, pending: false } : m,
          );
          return { messages: next, streaming: false };
        }
        case 'transcript': {
          // Live partial transcript surfaces in the composer, not here.
          return s;
        }
        case 'tool_call': {
          // Show a small italic note inline so the user sees the AI working.
          const next = [...s.messages];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].role === 'assistant' && next[i].pending) {
              const note = `\n\n_Вызов: ${ev.name}_\n\n`;
              next[i] = { ...next[i], content: next[i].content + note };
              break;
            }
          }
          return { messages: next };
        }
        case 'error': {
          return { streaming: false };
        }
        default:
          return s;
      }
    }),

  setSocketState: (socketState) => set({ socketState }),
  setRecording: (recording) => set({ recording }),
}));
